<template>
  <div
    class="conversations-list-wrap flex-basis-clamp flex-shrink-0 flex-basis-custom overflow-hidden flex flex-col border-r rtl:border-r-0 rtl:border-l border-slate-50 dark:border-slate-800/50"
    :class="{
      hide: !showConversationList,
      'list--full-width': isOnExpandedLayout,
    }"
  >
    <slot />
    <chat-list-actions />

    <p
      v-if="!chatListLoading && !conversationList.length"
      class="overflow-auto p-4 flex justify-center items-center"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <div
      ref="activeConversation"
      class="conversations-list flex-1"
      :class="{
        'overflow-hidden': isContextMenuOpen,
        'has-scroll': hasScroll,
      }"
      :dir="isRTLView ? 'rtl' : 'ltr'"
    >
      <div class="custom-scroll">
        <ConversationCard
          v-for="chat in conversationList"
          :key="chat.id"
          :active-label="label"
          :team-id="teamId"
          :folders-id="foldersId"
          :chat="chat"
          :conversation-type="conversationType"
          :show-assignee="showAssigneeInConversationCard"
          :selected="isConversationSelected(chat.id)"
          :pinned="isConversationPinned(chat.id)"
          :show-checkbox="isShowConversationCheckbox"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
          @assign-agent="onAssignAgent"
          @assign-team="onAssignTeam"
          @assign-label="onAssignLabels"
          @update-conversation-status="toggleConversationStatus"
          @context-menu-toggle="onContextMenuToggle"
          @mark-as-unread="markAsUnread"
          @assign-priority="assignPriority"
          @pin="pinConversation"
        />
      </div>
      <div v-if="chatListLoading" class="text-center">
        <span class="spinner mt-4 mb-4" />
      </div>
      <button
        v-if="
          !hasCurrentPageEndReached &&
          !chatListLoading &&
          conversationList.length
        "
        class="load-more--button"
        @click="loadMoreConversations"
      >
        {{ $t('CHAT_LIST.LOAD_MORE_CONVERSATIONS') }}
      </button>

      <p v-if="showEndOfListMessage" class="text-center text-muted p-4">
        {{ $t('CHAT_LIST.EOF') }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { computed } from 'vue';
import ChatListActions from './Actions.vue';
import ConversationCard from '../ConversationCard/Index.vue';
import timeMixin from 'dashboard/mixins/time.js';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import conversationMixin from 'dashboard/mixins/conversations.js';
import wootConstants from 'dashboard/constants/globals';
import adminMixin from 'dashboard/mixins/isAdmin.js';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator.js';
import alertMixin from 'shared/mixins/alertMixin';
import uiSettingsMixin, {
  CONVERSATION_LIST_TAB_GROUPS,
} from 'dashboard/mixins/uiSettings';
import rtlMixin from 'shared/mixins/rtlMixin';

import {
  hasPressedAltAndJKey,
  hasPressedAltAndKKey,
} from 'shared/helpers/KeyboardHelpers';

import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

export default {
  components: {
    ChatListActions,
    ConversationCard,
  },
  mixins: [
    timeMixin,
    conversationMixin,
    eventListenerMixins,
    adminMixin,
    alertMixin,
    uiSettingsMixin,
    rtlMixin,
  ],
  provide() {
    return {
      chatList: this,
      displayConversationCheckbox: this.displayConversationCheckbox,
      hideConversationCheckbox: this.hideConversationCheckbox,
      isShowConversationCheckbox: computed(
        () => this.isShowConversationCheckbox
      ),
    };
  },
  props: {
    conversationInbox: {
      type: [String, Number],
      default: 0,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showConversationList: {
      default: true,
      type: Boolean,
    },
    isOnExpandedLayout: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      activeAssigneeTab: null,
      activeStatusTab: null,
      activeSortBy: wootConstants.SORT_BY_TYPE.LATEST,
      filterResultIdSet: null,
      // chatsOnView is to store the chats that are currently visible on the screen,
      // which mirrors the conversationList.
      chatsOnView: [],
      selectedConversations: [],
      selectedInboxes: [],
      isContextMenuOpen: false,
      hasScroll: false,
      isShowConversationCheckbox: false,
      isMarkingAllRead: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      chatLists: 'getAllConversations',
      mineChatsList: 'getMineChats',
      newChatsList: 'getNewChats',
      allChatList: 'getAllStatusChats',
      unAssignedChatsList: 'getUnAssignedChats',
      chatListLoading: 'getChatListLoadingStatus',
      conversationStats: 'conversationStats/getStats',
      allConversationsAllowed: 'getAllConversationsAllowed',
      appliedFilters: 'getAppliedConversationFilters',
      folders: 'customViews/getCustomViews',
      inboxPermissions: 'getInboxPermissions', // d99d
      accountId: 'getCurrentAccountId',
    }),
    hasAppliedFilters() {
      return this.appliedFilters.length !== 0;
    },
    hasActiveFolders() {
      return this.activeFolder && this.foldersId > 0;
    },
    hasAppliedFiltersOrActiveFolders() {
      return this.hasAppliedFilters || this.hasActiveFolders;
    },
    savedFoldersValue() {
      if (this.hasActiveFolders) {
        // const payload = this.activeFolder.query;
        // this.fetchSavedFilteredConversations(payload);
        return this.foldersId;
      }
      return null;
    },
    showEndOfListMessage() {
      return (
        this.conversationList.length &&
        this.hasCurrentPageEndReached &&
        !this.chatListLoading
      );
    },
    currentUserDetails() {
      const { id, name } = this.currentUser;
      return {
        id,
        name,
      };
    },
    activeTab() {
      return this.conversationListTabGroup ===
        CONVERSATION_LIST_TAB_GROUPS.STATUS
        ? this.activeStatusTab
        : this.activeAssigneeTab;
    },
    defaultActiveStatusTab() {
      return (
        this.statusTabsOrder.find(i => i.visible)?.key ||
        wootConstants.STATUS_TYPE.ACTIVE
      );
    },
    defaultActiveAssigneeTab() {
      return (
        this.ownershipTabsOrder.find(i => i.visible)?.key ||
        wootConstants.ASSIGNEE_TYPE.ME
      );
    },
    activeStatus() {
      const isStatusTabGroup =
        this.conversationListTabGroup === CONVERSATION_LIST_TAB_GROUPS.STATUS;
      return isStatusTabGroup
        ? this.activeStatusTab
        : wootConstants.STATUS_TYPE.ACTIVE;
    },
    assigneeTabItems() {
      const ASSIGNEE_TYPE_TAB_KEYS = {
        me: 'mineCount',
        unassigned: 'unAssignedCount',
        all: 'allCount',
        new: 'newCount',
      };
      return (
        Object.keys(ASSIGNEE_TYPE_TAB_KEYS)
          // only admin can see all conversations (todo perhaps it is temp logic) //d99d
          .filter(
            key =>
              (key !== 'all' && key !== 'unassigned') ||
              this.allConversationsAllowed ||
              this.teamId ||
              this.inboxPermissions.find(
                permission => permission.name === 'Inbox read all conversations'
              )?.ids
          )
          .map(key => {
            const count =
              this.conversationStats[ASSIGNEE_TYPE_TAB_KEYS[key]] || 0;
            return {
              key,
              name: this.$t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
              count,
            };
          })
      );
    },
    showAssigneeInConversationCard() {
      return (
        this.hasAppliedFiltersOrActiveFolders ||
        this.conversationListTabGroup === CONVERSATION_LIST_TAB_GROUPS.STATUS ||
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ALL ||
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.NEW
      );
    },
    currentPaginationKey() {
      return this.conversationListTabGroup ===
        CONVERSATION_LIST_TAB_GROUPS.STATUS
        ? `status:${this.activeStatusTab}`
        : this.activeAssigneeTab;
    },
    currentPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.currentPaginationKey
      );
    },
    currentPageFilterKey() {
      return this.hasAppliedFiltersOrActiveFolders
        ? 'appliedFilters'
        : this.currentPaginationKey;
    },
    currentFiltersPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.currentPageFilterKey
      );
    },
    hasCurrentPageEndReached() {
      return this.$store.getters['conversationPage/getHasEndReached'](
        this.currentPageFilterKey
      );
    },
    activeAssigneeTabCount() {
      return this.assigneeTabItems.find(
        item => item.key === this.activeAssigneeTab
      )?.count;
    },
    conversationFilters() {
      const isStatusTabGroup =
        this.conversationListTabGroup === CONVERSATION_LIST_TAB_GROUPS.STATUS;
      return {
        inboxId: this.conversationInbox ? this.conversationInbox : undefined,
        assigneeType: isStatusTabGroup
          ? wootConstants.ASSIGNEE_TYPE.ALL
          : this.activeAssigneeTab,
        status: isStatusTabGroup
          ? this.activeStatusTab
          : wootConstants.STATUS_TYPE.ACTIVE,
        sortBy: this.activeSortBy,
        page: this.conversationListPagination,
        filterKey: this.currentPaginationKey,
        labels: this.label ? [this.label] : undefined,
        teamId: this.teamId || undefined,
        conversationType: this.conversationType || undefined,
        folders: this.hasActiveFolders ? this.savedFoldersValue : undefined,
        query: this.hasAppliedFilters
          ? filterQueryGenerator(this.appliedFilters)
          : undefined,
      };
    },
    conversationListPagination() {
      const conversationsPerPage = 25;
      if (
        this.conversationListTabGroup === CONVERSATION_LIST_TAB_GROUPS.STATUS
      ) {
        return (this.currentPage ?? 0) + 1;
      }
      const isNoFiltersOrFoldersAndChatListNotEmpty =
        !this.hasAppliedFiltersOrActiveFolders && this.chatsOnView !== [];
      const isUnderPerPage =
        this.chatsOnView.length < conversationsPerPage &&
        this.activeAssigneeTabCount < conversationsPerPage &&
        this.activeAssigneeTabCount > this.chatsOnView.length;

      if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
        return 1;
      }
      return this.currentPage + 1;
    },
    conversationList() {
      let conversationList = [];
      const filters = this.conversationFilters;

      let isOpenActiveFolderAndHasStatusInFilter =
        this.hasActiveFolders &&
        this.activeFolder.query.payload.find(i => i.attribute_key === 'status');
      let isApplyFilterHasStatusInFilter = this.appliedFilters?.find(
        i => i.attribute_key === 'status'
      );

      if (
        isOpenActiveFolderAndHasStatusInFilter ||
        isApplyFilterHasStatusInFilter
      ) {
        filters.status = 'all';
      }

      if (
        this.conversationListTabGroup === CONVERSATION_LIST_TAB_GROUPS.STATUS &&
        !this.hasAppliedFiltersOrActiveFolders
      ) {
        conversationList = [...this.allChatList(filters)];
      } else if (this.activeAssigneeTab === 'me') {
        conversationList = [...this.mineChatsList(filters)];
      } else if (this.activeAssigneeTab === 'unassigned') {
        conversationList = [...this.unAssignedChatsList(filters)];
      } else if (this.activeAssigneeTab === 'new') {
        conversationList = [...this.newChatsList(filters)];
      } else {
        conversationList = [...this.allChatList(filters)];
      }

      if (this.hasAppliedFiltersOrActiveFolders && this.filterResultIdSet) {
        conversationList = conversationList.filter(c =>
          this.filterResultIdSet.has(c.id)
        );
      }

      return conversationList;
    },
    activeFolder() {
      if (this.foldersId) {
        const activeView = this.folders.filter(
          view => view.id === Number(this.foldersId)
        );
        const [firstValue] = activeView;
        return firstValue;
      }
      return undefined;
    },
    activeTeam() {
      if (this.teamId) {
        return this.$store.getters['teams/getTeam'](this.teamId);
      }
      return {};
    },
    allConversationsSelected() {
      if (!this.conversationList.length) return false;
      return (
        this.conversationList.length === this.selectedConversations.length &&
        this.conversationList.every(el =>
          this.selectedConversations.includes(el.id)
        )
      );
    },
    uniqueInboxes() {
      return [...new Set(this.selectedInboxes)];
    },
    account() {
      return this.$store.getters['accounts/getAccount'](this.accountId);
    },
  },
  watch: {
    activeTeam() {
      this.resetAndFetchData();
    },
    conversationInbox() {
      this.resetAndFetchData();
    },
    label() {
      this.resetAndFetchData();
    },
    conversationType() {
      this.resetAndFetchData();
    },
    activeFolder() {
      this.resetAndFetchData();
    },
    chatLists() {
      this.chatsOnView = this.conversationList;
    },
    conversationList() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.conversations-list');
        this.hasScroll = el.scrollHeight > el.clientHeight;
      });
    },
    conversationListTabGroup(newVal) {
      this.resetBulkActions();
      if (newVal !== CONVERSATION_LIST_TAB_GROUPS.STATUS)
        this.activeStatusTab = wootConstants.STATUS_TYPE.ACTIVE;
      bus.$emit('clearSearchInput');
      if (!this.currentPage && !this.hasAppliedFilters) {
        this.fetchConversations();
      }
    },
  },
  mounted() {
    this.activeAssigneeTab = this.defaultActiveAssigneeTab;
    this.activeStatusTab = this.defaultActiveStatusTab;
    this.$store.dispatch('setChatStatusFilter', this.activeStatusTab);
    this.$store.dispatch('setChatSortFilter', this.activeSortBy);
    this.resetAndFetchData();

    if (this.hasActiveFolders) {
      this.$store.dispatch('campaigns/get');
    }

    const el = this.$el.querySelector('.conversations-list');
    this.hasScroll = el.scrollHeight > el.clientHeight;

    bus.$on('fetch_conversation_stats', this.onFetchConvStat);
  },
  // d99d
  destroyed() {
    bus.$off('fetch_conversation_stats', this.onFetchConvStat);
  },
  // .
  methods: {
    onFetchConvStat() {
      this.$store.dispatch('conversationStats/get', this.conversationFilters);
    },
    isConversationPinned(id) {
      return (this.uiSettings.pinnedConversations || []).includes(id);
    },
    getKeyboardListenerParams() {
      const allConversations = this.$refs.activeConversation.querySelectorAll(
        'div.conversations-list div.conversation'
      );
      const activeConversation = this.$refs.activeConversation.querySelector(
        'div.conversations-list div.conversation.active'
      );
      const activeConversationIndex = [...allConversations].indexOf(
        activeConversation
      );
      const lastConversationIndex = allConversations.length - 1;
      return {
        allConversations,
        activeConversation,
        activeConversationIndex,
        lastConversationIndex,
      };
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndJKey(e)) {
        const { allConversations, activeConversationIndex } =
          this.getKeyboardListenerParams();
        if (activeConversationIndex === -1) {
          allConversations[0].click();
        }
        if (activeConversationIndex >= 1) {
          allConversations[activeConversationIndex - 1].click();
        }
      }
      if (hasPressedAltAndKKey(e)) {
        const {
          allConversations,
          activeConversationIndex,
          lastConversationIndex,
        } = this.getKeyboardListenerParams();
        if (activeConversationIndex === -1) {
          allConversations[lastConversationIndex].click();
        } else if (activeConversationIndex < lastConversationIndex) {
          allConversations[activeConversationIndex + 1].click();
        }
      }
    },
    resetAndFetchData() {
      this.resetBulkActions();
      this.filterResultIdSet = null;
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.$store.dispatch('clearConversationFilters');
      if (this.hasActiveFolders) {
        const payload = this.activeFolder.query;
        this.fetchSavedFilteredConversations(payload);
      }
      if (this.foldersId) {
        return;
      }
      this.fetchConversations();
    },
    fetchConversations() {
      this.$store
        .dispatch('fetchAllConversations', this.conversationFilters)
        .then(() => {
          this.$emit('conversation-load');
          if (this.conversationType === 'mention') this.fetchUnreadCount();
        });
    },
    loadMoreConversations() {
      if (!this.hasAppliedFiltersOrActiveFolders) {
        this.fetchConversations();
      }
      if (this.hasActiveFolders) {
        const payload = this.activeFolder.query;
        this.fetchSavedFilteredConversations(payload);
      }
      if (this.hasAppliedFilters) {
        this.fetchFilteredConversations(this.appliedFilters);
      }
    },

    fetchFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      if (page === 1) this.filterResultIdSet = null;
      const currentConversationId = Number(this.$route.params.conversation_id);
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: filterQueryGenerator(payload),
          page,
          assigneeType: this.activeAssigneeTab,
        })
        .then(data => {
          const returnedIds = (data?.payload || []).map(c => c.id);
          this.filterResultIdSet = new Set([
            ...(this.filterResultIdSet || []),
            ...returnedIds,
          ]);
          if (
            !currentConversationId ||
            !this.filterResultIdSet.has(currentConversationId)
          ) {
            this.$emit('conversation-load');
          }
        });
      this.showAdvancedFilters = false;
    },

    fetchSavedFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      if (page === 1) this.filterResultIdSet = null;
      const currentConversationId = Number(this.$route.params.conversation_id);
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: payload,
          page,
          assigneeType: this.activeAssigneeTab,
        })
        .then(data => {
          const returnedIds = (data?.payload || []).map(c => c.id);
          this.filterResultIdSet = new Set([
            ...(this.filterResultIdSet || []),
            ...returnedIds,
          ]);
          if (
            !currentConversationId ||
            !this.filterResultIdSet.has(currentConversationId)
          ) {
            this.$emit('conversation-load');
          }
        });
    },
    updateAssigneeTab(selectedTab) {
      if (this.activeAssigneeTab !== selectedTab) {
        this.resetBulkActions();
        bus.$emit('clearSearchInput');
        this.activeAssigneeTab = selectedTab;

        if (!this.hasAppliedFiltersOrActiveFolders) {
          if (!this.currentPage) {
            this.fetchConversations();
          }
        } else if (this.hasActiveFolders) {
          this.$store.dispatch('conversationPage/reset');
          this.$store.dispatch('emptyAllConversations');
          const payload = this.activeFolder.query;
          this.fetchSavedFilteredConversations(payload);
        } else if (this.hasAppliedFilters) {
          this.$store.dispatch('conversationPage/reset');
          this.$store.dispatch('emptyAllConversations');
          this.fetchFilteredConversations(this.appliedFilters);
        }
      }
    },
    updateStatusTab(selectedTab) {
      if (this.activeStatusTab !== selectedTab) {
        this.resetBulkActions();
        bus.$emit('clearSearchInput');
        this.activeStatusTab = selectedTab;
        if (!this.currentPage) {
          this.fetchConversations();
        }
      }
    },
    updateTab({ group, key }) {
      if (group === CONVERSATION_LIST_TAB_GROUPS.STATUS) {
        this.updateStatusTab(key);
      } else {
        this.updateAssigneeTab(key);
      }
    },
    resetBulkActions() {
      this.selectedConversations = [];
      this.selectedInboxes = [];
    },
    isConversationSelected(id) {
      return this.selectedConversations.includes(id);
    },
    selectConversation(conversationId, inboxId) {
      this.selectedConversations.push(conversationId);
      this.selectedInboxes.push(inboxId);
    },
    deSelectConversation(conversationId, inboxId) {
      this.selectedConversations = this.selectedConversations.filter(
        item => item !== conversationId
      );
      this.selectedInboxes = this.selectedInboxes.filter(
        item => item !== inboxId
      );
    },
    selectAllConversations(check) {
      if (check) {
        this.selectedConversations = this.conversationList.map(item => item.id);
        this.selectedInboxes = this.conversationList.map(item => item.inbox_id);
      } else {
        this.resetBulkActions();
      }
    },
    // Same method used in context menu, conversationId being passed from there.
    async onAssignAgent(agent, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations,
          fields: {
            assignee_id: agent.id !== 0 ? agent.id : null,
          },
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL',
              {
                agentName: agent.name,
                conversationId,
              }
            )
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.ASSIGN_FAILED'));
      }
    },
    async assignPriority(priority, conversationId = null) {
      this.$store.dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
      this.$store
        .dispatch('assignPriority', { conversationId, priority })
        .then(() => {
          this.$track(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
            newValue: priority,
            from: 'Context menu',
          });
          this.showAlert(
            this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
              priority: priority
                ? this.$t(
                    `CONVERSATION.PRIORITY.OPTIONS.${priority.toUpperCase()}`
                  )
                : this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'), // d99d temp solution until they fix it
              conversationId,
            })
          );
        });
    },
    async markAsUnread(conversationId) {
      await this.$store.dispatch('markConversationUnread', {
        id: conversationId,
        foldersId: this.foldersId,
      });
      /* d99d commented out
      try {
        await this.$store.dispatch('markMessagesUnread', {
          id: conversationId,
        });
        const {
          params: { accountId, inbox_id: inboxId, label, teamId },
          name,
        } = this.$route;
        let conversationType = '';
        if (isOnMentionsView({ route: { name } })) {
          conversationType = 'mention';
        } else if (isOnUnattendedView({ route: { name } })) {
          conversationType = 'unattended';
        }
        this.$router.push(
          conversationListPageURL({
            accountId,
            conversationType: conversationType,
            customViewId: this.foldersId,
            inboxId,
            label,
            teamId,
          })
        );
      } catch (error) {
        // Ignore error
      }
       */
    },
    async onAssignTeam(team, conversationId = null) {
      try {
        await this.$store.dispatch('assignTeam', {
          conversationId,
          teamId: team.id,
        });
        this.showAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL',
            {
              team: team.name,
              conversationId,
            }
          )
        );
      } catch (error) {
        this.showAlert(
          this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED')
        );
      }
    },
    // Same method used in context menu, conversationId being passed from there.
    async onAssignLabels(labels, conversationId = null) {
      try {
        let labelsOperation = {
          add: labels,
        };
        if (conversationId) {
          const savedLabels =
            this.$store.getters['conversationLabels/getConversationLabels'](
              conversationId
            );
          const remove = [];
          const add = [];
          labels.forEach(label => {
            if (savedLabels.includes(label)) return remove.push(label);
            add.push(label);
          });
          labelsOperation = {
            add,
            remove,
          };
        }
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations,
          labels: labelsOperation,
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL',
              {
                labelName: labels[0],
                conversationId,
              }
            )
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
      }
    },
    async onAssignTeamsForBulk(team) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: this.selectedConversations,
          fields: {
            team_id: team.id,
          },
        });
        this.selectedConversations = [];
        this.showAlert(this.$t('BULK_ACTION.TEAMS.ASSIGN_SUCCESFUL'));
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.TEAMS.ASSIGN_FAILED'));
      }
    },
    async onUpdateConversations(status) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: this.selectedConversations,
          fields: {
            status,
          },
        });
        this.selectedConversations = [];
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
      }
    },
    async onMarkAsReadAll() {
      if (this.isMarkingAllRead) return;
      if (
        this.conversationList.every(
          c => !c.unread_count && !this.isManualUnread(c)
        )
      ) {
        this.showAlert(this.$t('BULK_ACTION.MARK_AS_READ_ALL_ALREADY_READ'));
        return;
      }
      this.isMarkingAllRead = true;
      try {
        await this.$store.dispatch('bulkActions/mark_as_read_all', {
          ids: this.conversationList.map(i => i.id),
        });
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
      } finally {
        this.isMarkingAllRead = false;
      }
    },
    toggleConversationStatus(conversationId, status, snoozedUntil) {
      this.$store
        .dispatch('toggleStatus', {
          conversationId,
          status,
          snoozedUntil,
        })
        .then(() => {
          this.showAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
          this.isLoading = false;
        });
    },
    allSelectedConversationsStatus(status) {
      if (!this.selectedConversations.length) return false;
      return this.selectedConversations.every(item => {
        if (status === 'active')
          return (
            this.$store.getters.getConversationById(item).status !== 'resolved'
          ); // d99d
        return this.$store.getters.getConversationById(item).status === status;
      });
    },
    onContextMenuToggle(state) {
      this.isContextMenuOpen = state;
    },
    fetchUnreadCount() {
      this.$store.dispatch('notifications/unReadCount', { filter: 'mentions' });
    },
    pinConversation(conversationId) {
      this.syncPinnedConversations(conversationId);
    },
    displayConversationCheckbox() {
      this.isShowConversationCheckbox = true;
    },
    hideConversationCheckbox() {
      this.isShowConversationCheckbox = false;
      this.selectAllConversations(false);
    },
  },
};
</script>
<style scoped>
@tailwind components;
@layer components {
  .flex-basis-clamp {
    flex-basis: clamp(20rem, 4vw + 21.25rem, 27.5rem);
  }
}
</style>

<style scoped lang="scss">
.conversations-list-wrap {
  box-shadow: 4px 0px 4px 0px #a3a3a31a;
  z-index: 12;

  &.hide {
    @apply hidden;
  }

  &.list--full-width {
    @apply basis-full;
  }
}

.conversations-list {
  @apply overflow-hidden hover:overflow-y-auto;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }

  &[dir='ltr'] {
    ::v-deep {
      &.has-scroll:not(.overflow-hidden):hover {
        .conversation {
          padding-right: 9px;
        }

        .load-more--button {
          padding-right: 13px;
        }
      }
    }
  }

  &[dir='rtl'] {
    ::v-deep {
      &.has-scroll:not(.overflow-hidden):hover {
        .conversation {
          padding-left: 9px;
        }

        .load-more--button {
          padding-left: 13px;
        }
      }
    }
  }
}

.load-more--button {
  @apply text-center rounded-none;
  display: inline-flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #2abb7f;
  width: 100%;
  cursor: pointer;

  &:hover {
    background: #f0fdf4;
    color: #1f845a;
  }
}
</style>
